import { useState, useCallback, useMemo, useRef } from "react";
import { SbNotification } from "uiCore";
import {
  getWorkTransformationSummary,
  getWorkTransformationTotalPotential,
  getWorkTransformationCollaboration,
  getWorkTransformationRoiLeaderboard,
  getWorkTransformationSkills,
} from "Api/apiService";

// Maps this dashboard's filter-dimension keys to the backend's `segment` query vocabulary.
// Only Function/Sub-Function differ — everything else lines up 1:1.
const DIMENSION_TO_SEGMENT = {
  legal_entity: "legal_entity",
  business_unit: "business_unit",
  function: "org_function",
  sub_function: "microtask_function",
  job_level: "job_level",
};

const DIMENSION_LABELS = {
  legal_entity: "Legal Entity",
  business_unit: "Business Unit",
  function: "Function",
  sub_function: "Sub-Function",
  job_level: "Job Level",
};

const isNotFound = (error) => error?.response?.status === 404;

const uniqueNames = (rows) => [...new Set((rows || []).map((r) => r.name))];

// Filter dropdown values come from the total-potential breakdown itself
// (always the org's full, unfiltered set of real dimension values) rather
// than a separate dimensions endpoint or hardcoded sample data.
const buildDimensions = (breakdown) => {
  if (!breakdown) return null;
  return {
    legal_entity: { label: DIMENSION_LABELS.legal_entity, values: uniqueNames(breakdown.legal_entity) },
    business_unit: { label: DIMENSION_LABELS.business_unit, values: uniqueNames(breakdown.business_unit) },
    function: { label: DIMENSION_LABELS.function, values: uniqueNames(breakdown.function) },
    sub_function: {
      label: DIMENSION_LABELS.sub_function,
      values: uniqueNames((breakdown.function || []).flatMap((f) => f.sub_function || [])),
    },
    job_level: { label: DIMENSION_LABELS.job_level, values: uniqueNames(breakdown.job_level) },
  };
};

const useWorkTransformationData = () => {
  const [loading, setLoading] = useState(true);
  // ROI is fetched separately from the other 4 widgets (see fetchRoiLeaderboard
  // below) so a slow/failed ROI call can't block them — but that also means
  // `loading` turns false while the ROI fetch is still in flight for a few
  // more seconds. roiLoading tracks that window separately so the ROI card
  // can show its own spinner instead of silently displaying the previous
  // filter's stale ROI number.
  const [roiLoading, setRoiLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [summary, setSummary] = useState(null);
  const [totalPotentialUsd, setTotalPotentialUsd] = useState(null);
  const [totalPotentialHours, setTotalPotentialHours] = useState(null);
  const [collaboration, setCollaboration] = useState(null);
  const [roiLeaderboard, setRoiLeaderboard] = useState(null);
  const [skills, setSkills] = useState(null);

  // Every filter change bumps this so a slower, earlier fetchFiltered's response
  // (Total Potential, Collaboration, Skills, ROI leaderboard all included) can't
  // clobber state set by a faster, later one once the user has already moved on.
  const requestIdRef = useRef(0);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await getWorkTransformationSummary();
      setSummary(data);
    } catch (error) {
      if (isNotFound(error)) {
        setNoData(true);
      } else {
        SbNotification.error("Failed to load dashboard summary", "Please try again later.");
      }
    }
  }, []);

  const fetchRoiLeaderboard = useCallback(async (segment, value, requestId) => {
    try {
      const data = await getWorkTransformationRoiLeaderboard({ limit: 10, segment, value });
      if (requestId !== requestIdRef.current) return;
      setRoiLeaderboard(data);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!isNotFound(error)) {
        SbNotification.error("Failed to load ROI leaderboard", "Please try again later.");
      }
    } finally {
      if (requestId === requestIdRef.current) setRoiLoading(false);
    }
  }, []);

  // Total Potential ($/hrs), Collaboration, ROI leaderboard, and Skills are
  // the endpoints whose numbers change with the active segment/value filter.
  const fetchFiltered = useCallback(
    async (segment, value) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setRoiLoading(true);
      try {
        const [usd, hours, collab, skillsData] = await Promise.all([
          getWorkTransformationTotalPotential({ metric: "dollar", segment, value }),
          getWorkTransformationTotalPotential({ metric: "hours", segment, value }),
          getWorkTransformationCollaboration({ segment, value }),
          getWorkTransformationSkills({ segment, value }),
        ]);
        if (requestId !== requestIdRef.current) return;
        setTotalPotentialUsd(usd);
        setTotalPotentialHours(hours);
        setCollaboration(collab);
        setSkills(skillsData);
        setNoData(false);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        if (isNotFound(error)) {
          setNoData(true);
        } else {
          SbNotification.error("Failed to load dashboard data", "Please try again later.");
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
      // ROI leaderboard is fetched independently so a slow/failed leaderboard
      // query can't block the other widgets from updating on filter change.
      fetchRoiLeaderboard(segment, value, requestId);
    },
    [fetchRoiLeaderboard],
  );

  const initialize = useCallback(() => {
    fetchSummary();
    fetchFiltered(undefined, undefined);
  }, [fetchSummary, fetchFiltered]);

  // `value` is the already-resolved dimension value string (the caller looks
  // it up from `dimensions[dim].values[activeIndex]`), not an index — this
  // hook doesn't know about the UI's index-based selection model.
  const refetchForFilter = useCallback(
    (dim, value) => {
      const segment = dim ? DIMENSION_TO_SEGMENT[dim] : undefined;
      fetchFiltered(segment, dim && value ? value : undefined);
    },
    [fetchFiltered],
  );

  const dimensions = useMemo(() => buildDimensions(totalPotentialUsd?.breakdown), [totalPotentialUsd]);

  return {
    loading,
    roiLoading,
    noData,
    summary,
    totalPotentialUsd,
    totalPotentialHours,
    collaboration,
    roiLeaderboard,
    skills,
    dimensions,
    initialize,
    refetchForFilter,
  };
};

export default useWorkTransformationData;
